import client from './client'
import type { AbsenceAttachment, AbsenceStatus, HalfDay } from '../types'
import type { Paginated } from './attendance'
import { deleteAbsence, type AbsenceStatusValue } from './attendance'

/**
 * Nepřítomnosti seskupené po žádostech (Docházka → Nepřítomnosti).
 *
 * V DB je nepřítomnost po dnech; server je skládá zpět do žádostí „od–do" (stejný pracovník, typ,
 * stav a zadání naráz) a stránkuje po žádostech se skutečným počtem (pagination.total).
 */

export interface AbsenceGroupDay {
  id: string
  date: string
  half_day: HalfDay | null
  hours: number
  attachments: AbsenceAttachment[]
}

export interface PlanningConflict {
  date: string
  order_id: string
  order_number: string
  order_name: string
  hours: number
}

export interface AbsenceGroup {
  key: string
  ids: string[]
  user: { id: string; name: string }
  type: {
    id: string | null
    code: string
    name: string
    color: string
    consumes_overtime: boolean
    counts_as_worked: boolean
  }
  status: AbsenceStatus
  status_label: string
  date_from: string
  date_to: string
  days_count: number
  hours_total: number
  days: AbsenceGroupDay[]
  notes: string[]
  attachments_count: number
  created_by: string
  created_at: string
  decided_by: string | null
  decided_at: string | null
  /** Důvod zamítnutí (jen u zamítnutých). */
  decision_note?: string | null
  /** Jen s with_checks: dny, kdy je pracovník naplánovaný na zakázku. */
  planning_conflicts?: PlanningConflict[]
  /** Jen s with_checks a u typů čerpajících přesčasy. */
  overtime?: { balance: number; pending: number; after_approval: number; allow_negative: boolean } | null
}

export interface AbsenceOverviewFilters {
  status?: AbsenceStatus | ''
  user_id?: string | null
  absence_type_id?: string | null
  date_from?: string
  date_to?: string
  page?: number
  per_page?: number
  with_checks?: boolean
}

export async function getAbsenceOverview(filters: AbsenceOverviewFilters): Promise<Paginated<AbsenceGroup>> {
  const params: Record<string, string | number> = {}
  Object.entries(filters).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || v === false) return
    params[k] = v === true ? 1 : (v as string | number)
  })
  const response = await client.get('/attendance/absences-overview', { params })
  return response.data.data
}

/**
 * Nastaví stav všem dnům žádosti (každý den je v DB samostatný záznam) jedním voláním — server
 * u každého dne přepočítá přesčasy a zapíše audit, pracovník dostane jednu notifikaci za žádost.
 * Vrací počet dnů, které se nepodařilo změnit (a chybovou hlášku).
 */
export async function setAbsenceDaysStatus(ids: string[], status: AbsenceStatusValue, note?: string): Promise<{ failed: number; error: string | null }> {
  try {
    await client.post('/attendance/absences-status', { ids, status, note: status === 'rejected' && note ? note : undefined })
    return { failed: 0, error: null }
  } catch (e: any) {
    return { failed: ids.length, error: e?.response?.data?.error ?? 'Změna stavu se nezdařila' }
  }
}

/** Smaže dny žádosti (jen se schopností „Nepřítomnosti za ostatní"). */
export async function deleteAbsenceDays(ids: string[]): Promise<{ failed: number; error: string | null }> {
  let failed = 0
  let error: string | null = null
  for (const id of ids) {
    try {
      await deleteAbsence(id)
    } catch (e: any) {
      failed++
      error ??= e?.response?.data?.error ?? 'Smazání se nezdařilo'
    }
  }
  return { failed, error }
}
